import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    RefreshCw, Plus, Trash2, X,
    Monitor, Globe, Users, AlertTriangle,
    Bell, Lock, Search,
    Clock, ChevronRight, UserCog, Wallet
} from "lucide-react";
import { SiJira } from "react-icons/si";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { getRole } from "../../utils/authService";
import { toast } from "../../utils/toast";
import { displayName } from "../../utils/displayName";
import { PillSelect } from "../common/ui.jsx";
import ScreenshotSettings from "../admin/ScreenshotSettings.jsx";
import SsoSettings from "../admin/SsoSettings.jsx";
import "./productivity-config.css";

const NON_PROD = { value: "NON_PRODUCTIVE", label: "Non-Productive", color: "var(--color-danger)", bg: "rgba(231,76,60,0.12)" };

const AVATAR_COLORS = ["#00b4d8","#2ecc71","#e74c3c","#f39c12","#9b59b6","#1e8ef1"];
const avatarColor = (s) => AVATAR_COLORS[(s?.length||0) % AVATAR_COLORS.length];

const TABS = [
    { key: "teams",       label: "Teams"       },
    { key: "apps",        label: "Apps"        },
    { key: "alerts",      label: "Alerts"      },
    { key: "attendance",  label: "Attendance"  },
    { key: "screenshots", label: "Screenshots" },
    { key: "sso",         label: "Authentication" },
    // { key: "integrations",label: "Integrations" }, // commented out — not ready yet, keep for later
];

// Both links only need canManageProductivitySettings — isPathAllowedForRole accepts
// either cfo_dashboard or productivity_config for /cfo-salary-config specifically, so
// Configuration Settings alone is enough to reach Salaries too, same as Accounts.
const NAV_LINKS = [
    { key: "accounts", label: "Accounts", icon: UserCog, path: "/role-accounts"    },
    { key: "salaries",  label: "Salaries", icon: Wallet,  path: "/cfo-salary-config" },
];

// Review-only for now — no click handler wired up yet.
const INTEGRATIONS = [
    { key: "jira", label: "Jira", icon: SiJira },
];

function getAllowedViews() {
    try {
        const raw = JSON.parse(sessionStorage.getItem("allowedViews") || "[]");
        return Array.isArray(raw) ? raw : [];
    } catch {
        return [];
    }
}

export default function ProductivityConfig() {
    const role = getRole();
    // Reaching this page at all already required "productivity_config" to be granted
    // (client/admin, or a custom role template) — the Attendance tab and the
    // Accounts/Salaries quick-links used to be extra client/admin-only carve-outs on
    // top of that; canManageProductivitySettings treats the page-level grant as
    // sufficient instead of re-checking the literal role.
    const canManageProductivitySettings = role === "client" || role === "admin" || getAllowedViews().includes("productivity_config");
    const navigate = useNavigate();
    const location = useLocation();
    // Deep-linkable so the Google Drive OAuth redirect can land directly on
    // the Screenshots tab (?tab=screenshots) instead of defaulting to Teams.
    const [tab, setTab] = useState(() => {
        const requested = new URLSearchParams(window.location.search).get("tab");
        return TABS.some((t) => t.key === requested) ? requested : "teams";
    });

    // The useState initializer above only runs once, at mount — a link to
    // /settings?tab=integrations navigated to while already sitting on
    // /settings (e.g. from the topnav search) doesn't remount this
    // component, so the initializer never re-runs and `tab` silently stays
    // wherever it was. This re-syncs it whenever the URL's own ?tab= changes.
    useEffect(() => {
        const requested = new URLSearchParams(location.search).get("tab");
        if (requested && TABS.some((t) => t.key === requested)) {
            setTab((prev) => (prev === requested ? prev : requested));
        }
    }, [location.search]);

    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    // data
    const [apps, setApps]         = useState([]);
    const [urls, setUrls]         = useState([]);
    const [depts, setDepts]       = useState([]);
    const [agents, setAgents]     = useState([]);
    const [mappings, setMappings] = useState([]);

    // label tab
    const [teamId, setTeamId]         = useState("");
    const [labelSub, setLabelSub]     = useState("apps");
    const [search, setSearch]         = useState("");
    const [labelFilter, setLabelFilter] = useState("all");

    // alerts tab
    const [alertConfigs, setAlertConfigs] = useState([]);
    const [quickMins, setQuickMins]       = useState(60);
    const [alertScope, setAlertScope]     = useState("");

    // modals
    const [showCreate, setShowCreate]     = useState(false);
    const [newTeam, setNewTeam]           = useState({ name:"", description:"" });
    const [showAssign, setShowAssign]     = useState(false);
    const [assignTeamId, setAssignTeamId] = useState("");
    const [assignIds, setAssignIds]       = useState([]);
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [newUrl, setNewUrl]             = useState({ domainName:"", urlPattern:"", patternType:"EXACT", category:"" });

    // settings tab
    const [attLoading, setAttLoading]       = useState(false);
    const [workStart, setWorkStart]         = useState("09:00");
    const [workEnd, setWorkEnd]             = useState("17:00");
    const [workDays, setWorkDays]           = useState([1,2,3,4,5]);
    const [triggerUrls, setTriggerUrls]     = useState([]);
    const [attendanceMethod, setAttendanceMethod] = useState("url_trigger");
    const [triggerInput, setTriggerInput] = useState("");
    const [resetAgentId, setResetAgentId] = useState("");
    const [resetting, setResetting]       = useState(false);
    const [resetTeamId, setResetTeamId]   = useState("all");
    const [resetSearch, setResetSearch]   = useState("");

    // ── fetch ───────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [aR,uR,dR,agR,mR] = await Promise.all([
                apiGet("/api/productivity/apps"),
                apiGet("/api/productivity/urls"),
                apiGet("/api/departments"),
                apiGet("/api/agents"),
                apiGet("/api/productivity/mapping"),
            ]);
            const [aD,uD,dD,agD,mD] = await Promise.all([aR,uR,dR,agR,mR].map(r=>r.json().catch(()=>({}))));
            if (aD.success) setApps(aD.apps||[]);
            if (uD.success) setUrls(uD.urls||[]);
            if (dD.success) setDepts(dD.departments||[]);
            if (Array.isArray(agD)) setAgents(agD);
            if (mD.success) setMappings(mD.mappings||[]);
        } catch { toast.error("Failed to load"); }
        finally  { setLoading(false); }
    }, []);

    const fetchAlertData = useCallback(async () => {
        try {
            const cR = await apiGet("/api/productivity/alert-config");
            const cD = await cR.json().catch(()=>({}));
            if (cD.success) setAlertConfigs(cD.configs||[]);
        } catch {}
    }, []);

    const fetchSettings = useCallback(async () => {
        setAttLoading(true);
        try {
            const res = await apiGet("/api/analytics/attendance-settings");
            const d   = await res.json().catch(()=>null);
            if (d?.success) {
                setWorkStart(d.workStart||"09:00");
                setWorkEnd(d.workEnd||"17:00");
                setWorkDays(d.workingDays??[1,2,3,4,5]);
                setTriggerUrls(d.attendanceTriggerUrls||[]);
                setAttendanceMethod(d.attendanceMethod||"url_trigger");
            }
        } catch {} finally { setAttLoading(false); }
    }, []);


    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { if (depts.length>0&&!teamId) setTeamId(depts[0]._id); }, [depts]);
    // Clear the selection if it falls outside the active team/search filters,
    // so the hidden dropdown option can't stay silently selected.
    useEffect(() => {
        if (resetAgentId && !resetFilteredAgents.some(a=>a.agentId===resetAgentId)) {
            setResetAgentId("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetTeamId, resetSearch]);
    useEffect(() => {
        if (tab==="alerts")     { fetchAlertData(); }
        if (tab==="attendance") { if(canManageProductivitySettings) fetchSettings(); }
    }, [tab]);

    // ── sync ────────────────────────────────────────────────
    const handleSync = async () => {
        setSyncing(true);
        try {
            const [aR,uR] = await Promise.all([
                apiPost("/api/productivity/sync-apps"),
                apiPost("/api/productivity/sync-urls"),
            ]);
            const [aD,uD] = await Promise.all([aR,uR].map(r=>r.json().catch(()=>({}))));
            const newApps = aD.created||0;
            const total   = (aD.total||0)+(uD.total||0);
            if (newApps>0) toast.success(`Found ${newApps} new apps — label them now.`);
            else if (total>0) toast.success(`${total} apps already up to date.`);
            else toast.success("No apps yet — connect agents first.");
            await fetchAll();
            // getProdType moved out of handleSync
            if (newApps>0) setTab("apps");
        } catch { toast.error("Scan failed"); }
        finally  { setSyncing(false); }
    };

    // ── mappings ─────────────────────────────────────────────
    const getMapping = (type, name, scopeId) =>
        mappings.find(m=>m.entityType===type&&m.entityName?.toLowerCase()===name.toLowerCase()&&m.scopeType==="TEAM"&&m.scopeId===scopeId);

    // Helper to get productivity type for an entity (app or URL)
    const getProdType = (entityType, name) => {
        const m = getMapping(entityType, name, teamId);
        return m?.productivityType || "PRODUCTIVE";
    };
    const setLabel = async (entityType, name, id, prodType) => {
        if (!teamId) return toast.error("Select a team first");
        const teamName = depts.find(d=>d._id===teamId)?.name||"";
        try {
            const res  = await apiPost("/api/productivity/mapping", { entityType, entityId:id, entityName:name, productivityType:prodType, scopeType:"TEAM", scopeId:teamId, scopeName:teamName });
            const data = await res.json();
            if (data.success) {
                setMappings(prev=>{
                    const idx=prev.findIndex(m=>m.entityType===entityType&&m.entityName?.toLowerCase()===name.toLowerCase()&&m.scopeType==="TEAM"&&m.scopeId===teamId);
                    return idx>=0 ? prev.map((m,i)=>i===idx?data.mapping:m) : [...prev,data.mapping];
                });
            } else toast.error(data.message||"Failed");
        } catch { toast.error("Failed to save"); }
    };

    const clearLabel = async (mappingId) => {
        try {
            const res = await apiDelete(`/api/productivity/mapping/${mappingId}`);
            const d   = await res.json();
            if (d.success) setMappings(prev=>prev.filter(m=>m._id!==mappingId));
        } catch { toast.error("Failed"); }
    };

    // ── teams ────────────────────────────────────────────────
    const handleCreateTeam = async () => {
        if (!newTeam.name.trim()) return toast.error("Name required");
        try {
            const res  = await apiPost("/api/departments",{name:newTeam.name.trim(),description:newTeam.description});
            const data = await res.json();
            if (data.success) {
                toast.success(`"${data.department.name}" created`);
                setShowCreate(false);
                setNewTeam({name:"",description:""});
                await fetchAll();
                openAssign(data.department._id);
            } else toast.error(data.message||"Failed");
        } catch { toast.error("Failed"); }
    };

    const handleDeleteTeam = async (id) => {
        if (!confirm("Delete this team?")) return;
        try {
            const res  = await apiDelete(`/api/departments/${id}`);
            const data = await res.json();
            if (data.success) { toast.success("Deleted"); fetchAll(); }
        } catch { toast.error("Failed"); }
    };

    const openAssign = (id) => {
        const dept = depts.find(d=>d._id===id);
        setAssignTeamId(id); setAssignIds(dept?.agents||[]); setShowAssign(true);
    };

    const saveAssign = async () => {
        try {
            const res  = await apiPut("/api/departments/agents",{departmentId:assignTeamId,agentIds:assignIds});
            const data = await res.json();
            if (data.success) { toast.success("Saved"); setShowAssign(false); fetchAll(); }
            else toast.error(data.message||"Failed");
        } catch { toast.error("Failed"); }
    };

    const unassigned = () => {
        const all = new Set(); depts.forEach(d=>(d.agents||[]).forEach(a=>all.add(a)));
        return agents.filter(a=>!all.has(a.agentId));
    };

    // ── alerts ───────────────────────────────────────────────
    const saveAlert = async () => {
        try {
            const res  = await apiPost("/api/productivity/alert-config",{departmentId:alertScope||null,nonProductiveThresholdMinutes:Number(quickMins),severity:"WARNING"});
            const data = await res.json();
            if (data.success) { toast.success("Alert saved"); fetchAlertData(); }
            else toast.error("Failed");
        } catch { toast.error("Failed"); }
    };

    // ── settings ─────────────────────────────────────────────
    const saveHours = async () => {
        try {
            await apiPost("/api/analytics/work-hours",{workStart,workEnd});
            await apiPost("/api/analytics/work-calendar",{workingDays:workDays,holidays:[]});
            toast.success("Saved");
        } catch { toast.error("Failed"); }
    };

    const saveTriggers = async () => {
        try {
            const res = await apiPost("/api/analytics/attendance-triggers",{attendanceTriggerUrls:triggerUrls});
            if (res.ok) toast.success("Saved"); else toast.error("Failed");
        } catch { toast.error("Failed"); }
    };

    const saveAttendanceMethod = async (method) => {
        try {
            const res = await apiPost("/api/analytics/attendance-method", { attendanceMethod: method });
            if (res.ok) { setAttendanceMethod(method); toast.success("Attendance method saved"); }
            else toast.error("Failed to save");
        } catch { toast.error("Failed to save"); }
    };

    const resetFilteredAgents = agents.filter(a=>{
        if (resetTeamId!=="all") {
            const team = depts.find(d=>d.name===resetTeamId);
            if (!team?.agents?.includes(a.agentId)) return false;
        }
        if (resetSearch.trim() && !displayName(a).toLowerCase().includes(resetSearch.trim().toLowerCase())) return false;
        return true;
    });

    const resetTodayAttendance = async () => {
        if (!resetAgentId) { toast.error("Select an agent first"); return; }
        const now = new Date();
        const today = now.getFullYear()+"-"+String(now.getMonth()+1).padStart(2,"0")+"-"+String(now.getDate()).padStart(2,"0");
        setResetting(true);
        try {
            const res = await apiDelete(`/api/analytics/attendance/${resetAgentId}/${today}`);
            if (res.ok) toast.success(`Today's attendance cleared for ${resetAgentId}`);
            else toast.error("Failed to reset");
        } catch { toast.error("Failed to reset"); }
        finally { setResetting(false); }
    };

    const addTrigger = () => {
        const val = triggerInput.trim();
        if (!val) return;
        if (!triggerUrls.includes(val)) setTriggerUrls(prev=>[...prev,val]);
        setTriggerInput("");
    };

    // ── derived ──────────────────────────────────────────────
    const teamMaps    = teamId ? mappings.filter(m=>m.scopeType==="TEAM"&&m.scopeId===teamId&&m.productivityType==="NON_PRODUCTIVE") : [];
    const labeledApps = new Set(teamMaps.filter(m=>m.entityType==="APPLICATION").map(m=>m.entityName?.toLowerCase()));
    const labeledUrls = new Set(teamMaps.filter(m=>m.entityType==="URL").map(m=>m.entityName?.toLowerCase()));

    const getLabeledCount = (deptId) =>
        new Set(mappings.filter(m=>m.scopeType==="TEAM"&&m.scopeId===deptId&&m.entityType==="APPLICATION"&&m.productivityType==="NON_PRODUCTIVE").map(m=>m.entityName?.toLowerCase())).size;

    const visApps = apps.filter(a=>{
        if (search&&!a.appName.toLowerCase().includes(search.toLowerCase())) return false;
        if (labelFilter==="unlabeled") return !labeledApps.has(a.appName.toLowerCase());
        if (labelFilter==="labeled")   return  labeledApps.has(a.appName.toLowerCase());
        return true;
    }).sort((a,b)=>{
        const order = {NON_PRODUCTIVE:0, PRODUCTIVE:1};
        const aType = getProdType("APPLICATION", a.appName);
        const bType = getProdType("APPLICATION", b.appName);
        return order[aType] - order[bType];
    });
    const visUrls = urls.filter(u=>{
        if (search&&!u.domainName.toLowerCase().includes(search.toLowerCase())) return false;
        if (labelFilter==="unlabeled") return !labeledUrls.has(u.domainName.toLowerCase());
        if (labelFilter==="labeled")   return  labeledUrls.has(u.domainName.toLowerCase());
        return true;
    }).sort((a,b)=>{
        const order = {NON_PRODUCTIVE:0, PRODUCTIVE:1};
        const aType = getProdType("URL", a.domainName);
        const bType = getProdType("URL", b.domainName);
        return order[aType] - order[bType];
    });

    // ── loading ──────────────────────────────────────────────
    if (loading) return (
        <div className="pc-loading">
            <RefreshCw size={22} className="pc-spin" />
            <span>Loading…</span>
        </div>
    );

    return (
        <div className="pc-wrap">
            {/* ── Header ── */}
            <div className="pc-header">
                <div>
                    <h1>Productivity Setup</h1>
                    <p>Configure teams, app labels, and alerts</p>
                </div>
                <button className="gc-icon-btn" onClick={handleSync} disabled={syncing} data-tip={syncing ? "Refreshing…" : "Refresh"}>
                    <RefreshCw size={14} className={syncing?"pc-spin":""}/>
                </button>
            </div>

            {/* ── Tabs ── */}
            <nav className="pc-nav">
                {TABS.map(t=>(
                    <button
                        key={t.key}
                        className={`pc-tab${tab===t.key?" active":""}`}
                        onClick={()=>setTab(t.key)}
                    >
                        {t.label}
                        {t.key==="teams"   && depts.length>0       && <span className="pc-tab-badge">{depts.length}</span>}
                        {t.key==="apps"    && apps.length>0         && <span className="pc-tab-badge">{apps.length}</span>}
                        {t.key==="alerts"  && alertConfigs.length>0 && <span className="pc-tab-badge">{alertConfigs.length}</span>}
                    </button>
                ))}
                {canManageProductivitySettings&&(
                    <div className="pc-nav-links">
                        {NAV_LINKS.map(l=>(
                            <button key={l.key} className="pc-nav-link" onClick={()=>navigate(l.path)}>
                                <l.icon size={13}/> {l.label}
                            </button>
                        ))}
                    </div>
                )}
            </nav>

            {/* ════ TEAMS ════ */}
            {tab==="teams"&&(
                <div className="pc-section">
                    <div className="pc-section-bar">
                        <span className="pc-section-title">Teams ({depts.length})</span>
                        <button className="pc-btn primary" onClick={()=>setShowCreate(true)}>
                            <Plus size={13}/> New Team
                        </button>
                    </div>

                    {unassigned().length>0&&(
                        <div className="pc-alert-banner">
                            <AlertTriangle size={14}/>
                            {unassigned().length} agent{unassigned().length>1?"s":""} not assigned to any team
                        </div>
                    )}

                    {depts.length===0?(
                        <div className="pc-empty">
                            <Users size={32} style={{opacity:0.2,marginBottom:10}}/>
                            <p>No teams yet. Create one to get started.</p>
                            <button className="pc-btn primary" style={{marginTop:12}} onClick={()=>setShowCreate(true)}>
                                <Plus size={13}/> Create Team
                            </button>
                        </div>
                    ):(
                        <div className="pc-team-list">
                            {depts.map(dept=>{
                                const ta  = dept.agents||[];
                                const lc  = getLabeledCount(dept._id);
                                const col = avatarColor(dept.name);
                                return (
                                    <div className="pc-team-row" key={dept._id}>
                                        <div className="pc-team-avatar" style={{background:`${col}22`,color:col}}>
                                            {dept.name[0]?.toUpperCase()}
                                        </div>
                                        <div className="pc-team-info">
                                            <span className="pc-team-name">{dept.name}</span>
                                            {dept.description&&<span className="pc-team-desc">{dept.description}</span>}
                                        </div>
                                        <div className="pc-team-meta">
                                            <span className="pc-meta-chip">{ta.length} agent{ta.length!==1?"s":""}</span>
                                            <span className="pc-meta-chip">{lc} labeled</span>
                                        </div>
                                        <div className="pc-team-actions">
                                            <button className="pc-btn" onClick={()=>openAssign(dept._id)}>
                                                <Users size={12}/> Agents
                                            </button>
                                            <button className="pc-btn" onClick={()=>{setTeamId(dept._id);setTab("apps");}}>
                                                Label Apps <ChevronRight size={12}/>
                                            </button>
                                            <button className="pc-btn danger icon-btn" onClick={()=>handleDeleteTeam(dept._id)}>
                                                <Trash2 size={13}/>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ════ APPS ════ */}
            {tab==="apps"&&(
                <div className="pc-section">
                    {/* Team selector */}
                    {depts.length>1&&(
                        <div className="pc-section-bar" style={{marginBottom:"0.9rem"}}>
                            <span className="pc-section-title">Team</span>
                            <select
                                className="pc-select-inline"
                                value={teamId}
                                onChange={e=>setTeamId(e.target.value)}
                            >
                                {depts.map(d=><option key={d._id} value={d._id}>{d.name}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Sub-tabs + filters */}
                    <div className="pc-app-bar">
                        <div className="pc-sub-tabs">
                            <button className={`pc-sub-tab${labelSub==="apps"?" active":""}`}
                                onClick={()=>{setLabelSub("apps");setSearch("");setLabelFilter("all");}}>
                                <Monitor size={13}/> Apps <span className="pc-count">{apps.length}</span>
                            </button>
                            <button className={`pc-sub-tab${labelSub==="urls"?" active":""}`}
                                onClick={()=>{setLabelSub("urls");setSearch("");setLabelFilter("all");}}>
                                <Globe size={13}/> Websites <span className="pc-count">{urls.length}</span>
                            </button>
                        </div>
                        <div className="pc-app-filters">
                            <input
                                className="pc-search-input"
                                placeholder="Search…"
                                value={search}
                                onChange={e=>setSearch(e.target.value)}
                            />
                            {["all","unlabeled","labeled"].map(f=>(
                                <button key={f}
                                    className={`pc-filter-btn${labelFilter===f?" active":""}`}
                                    onClick={()=>setLabelFilter(f)}>
                                    {f==="all"?"All":f==="unlabeled"?"Productive":"Non-Productive"}
                                </button>
                            ))}
                            {labelSub==="urls"&&(
                                <button className="pc-btn primary" style={{marginLeft:"auto"}} onClick={()=>setShowUrlModal(true)}>
                                    <Plus size={12}/> Add
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Progress */}
                    {teamId&&apps.length>0&&(
                        <div className="pc-progress-bar">
                            <div className="pc-progress-fill" style={{width:`${(labeledApps.size/apps.length)*100}%`}}/>
                            <span className="pc-progress-label">
                                {labeledApps.size} of {apps.length} apps flagged as non-productive
                            </span>
                        </div>
                    )}

                    {(labelSub === "apps" ? apps.length : urls.length) === 0 ? (
                        <div className="pc-empty">
                            {labelSub === "apps" ? (
                                <>
                                    <Monitor size={28} style={{opacity:0.2,marginBottom:10}}/>
                                    <p>No apps found. Click <strong>Refresh</strong> to discover apps running on your agents.</p>
                                </>
                            ) : (
                                <>
                                    <Globe size={28} style={{opacity:0.2,marginBottom:10}}/>
                                    <p>No websites found. Add websites to get started.</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="pc-app-grid-scroll">
                            {labelSub === "apps" ? (
                                <table className="pc-table pc-apps-table">
                                    <thead>
                                        <tr>
                                            <th>App Name</th>
                                            <th>Category</th>
                                            <th>Executable</th>
                                            <th style={{ width: "180px" }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visApps.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="pc-empty">
                                                    {labelFilter === "unlabeled" ? "All apps labeled! ✓" : "No apps match your search."}
                                                </td>
                                            </tr>
                                        ) : (
                                            visApps.map(app => {
                                                const m      = teamId?getMapping("APPLICATION",app.appName,teamId):null;
                                                const isNP   = m?.productivityType === "NON_PRODUCTIVE";
                                                const col    = avatarColor(app.appName);
                                                return (
                                                    <tr key={app._id} className={isNP?"labeled-row":""} style={isNP?{backgroundColor:NON_PROD.bg}:{}}>
                                                        <td>
                                                            <div className="pc-app-cell">
                                                                <div className="pc-app-icon" style={{background:`${col}22`,color:col}}>
                                                                    {app.appName[0]?.toUpperCase()}
                                                                </div>
                                                                <span className="pc-app-name-cell" title={app.appName}>{app.appName}</span>
                                                            </div>
                                                        </td>
                                                        <td><span className="pc-table-cat">{app.category||"Other"}</span></td>
                                                        <td><span className="pc-table-exec">{app.executableName||"—"}</span></td>
                                                        <td>
                                                            <select
                                                                className={`pc-prod-select${isNP?" pc-prod-select--np":""}`}
                                                                value={isNP?"NON_PRODUCTIVE":"PRODUCTIVE"}
                                                                disabled={!teamId}
                                                                onChange={e=>e.target.value==="NON_PRODUCTIVE"
                                                                    ?setLabel("APPLICATION",app.appName,app._id,"NON_PRODUCTIVE")
                                                                    :(m&&clearLabel(m._id))}>
                                                                <option value="PRODUCTIVE">Productive</option>
                                                                <option value="NON_PRODUCTIVE">Non-Productive</option>
                                                            </select>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="pc-table pc-apps-table">
                                    <thead>
                                        <tr>
                                            <th>Website Domain</th>
                                            <th>Category</th>
                                            <th>Pattern</th>
                                            <th style={{ width: "180px" }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visUrls.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="pc-empty">
                                                    {labelFilter === "unlabeled" ? "All websites labeled! ✓" : "No websites match your search."}
                                                </td>
                                            </tr>
                                        ) : (
                                            visUrls.map(url => {
                                                const m    = teamId?getMapping("URL",url.domainName,teamId):null;
                                                const isNP = m?.productivityType === "NON_PRODUCTIVE";
                                                const col  = avatarColor(url.domainName);
                                                return (
                                                    <tr key={url._id} className={isNP?"labeled-row":""} style={isNP?{backgroundColor:NON_PROD.bg}:{}}>
                                                        <td>
                                                            <div className="pc-app-cell">
                                                                <div className="pc-app-icon" style={{background:`${col}22`,color:col}}>
                                                                    {url.domainName[0]?.toUpperCase()}
                                                                </div>
                                                                <span className="pc-app-name-cell" title={url.domainName}>{url.domainName}</span>
                                                            </div>
                                                        </td>
                                                        <td><span className="pc-table-cat">{url.category||"Uncategorized"}</span></td>
                                                        <td>
                                                            <span className="pc-table-exec">
                                                                {url.patternType==="EXACT"?"Exact":`${url.patternType}: ${url.urlPattern}`}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className={`pc-prod-select${isNP?" pc-prod-select--np":""}`}
                                                                value={isNP?"NON_PRODUCTIVE":"PRODUCTIVE"}
                                                                disabled={!teamId}
                                                                onChange={e=>e.target.value==="NON_PRODUCTIVE"
                                                                    ?setLabel("URL",url.domainName,url._id,"NON_PRODUCTIVE")
                                                                    :(m&&clearLabel(m._id))}>
                                                                <option value="PRODUCTIVE">Productive</option>
                                                                <option value="NON_PRODUCTIVE">Non-Productive</option>
                                                            </select>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ════ ALERTS ════ */}
            {tab==="alerts"&&(
                <div className="pc-section">
                    <div className="pc-alert-layout">
                        {/* Left: config form */}
                        <div className="pc-card pc-alert-config-card">
                            <div className="pc-card-head">
                                <Bell size={15} color="var(--color-warning)"/>
                                <h3>Distraction Alert</h3>
                            </div>
                            <p className="pc-card-desc">Alert when a team member spends too long on non-productive apps.</p>
                            <div className="pc-alert-form">
                                <div className="pc-alert-form-row">
                                    <label className="pc-label">Threshold</label>
                                    <div className="pc-inline-row">
                                        <span style={{fontSize:13,color:"var(--text-secondary)"}}>More than</span>
                                        <input type="number" className="pc-number-input"
                                            value={quickMins} min={1} max={480}
                                            onChange={e=>setQuickMins(Number(e.target.value))}/>
                                        <span style={{fontSize:13,color:"var(--text-secondary)"}}>min / day</span>
                                    </div>
                                </div>
                                <div className="pc-alert-form-row">
                                    <label className="pc-label">Apply to</label>
                                    <select className="pc-select" value={alertScope} onChange={e=>setAlertScope(e.target.value)}>
                                        <option value="">All teams</option>
                                        {depts.map(d=><option key={d._id} value={d._id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <button className="pc-btn primary pc-full-btn" style={{marginTop:4}} onClick={saveAlert}>
                                    <Bell size={13}/> Save Alert
                                </button>
                            </div>
                        </div>

                        {/* Right: active alerts */}
                        <div className="pc-card pc-alert-active-card">
                            <div className="pc-card-head">
                                <AlertTriangle size={15} color="var(--color-warning)"/>
                                <h3>Active Rules</h3>
                                <span className="pc-tab-badge" style={{marginLeft:"auto"}}>{alertConfigs.length}</span>
                            </div>
                            {alertConfigs.length===0?(
                                <div className="pc-empty" style={{padding:"2rem 0"}}>
                                    <p style={{fontSize:"0.8rem"}}>No active alert rules.</p>
                                </div>
                            ):(
                                <div className="pc-active-list" style={{borderTop:"none",paddingTop:4,marginTop:4}}>
                                    {alertConfigs.map(c=>(
                                        <div key={c._id} className="pc-active-item">
                                            <AlertTriangle size={11} color={c.severity==="CRITICAL"?"var(--color-danger)":"var(--color-warning)"}/>
                                            <span style={{fontWeight:700}}>{c.nonProductiveThresholdMinutes} min / day</span>
                                            <span className="pc-muted">{c.departmentId?depts.find(d=>d._id===c.departmentId?.toString())?.name||"Team":"All teams"}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ════ ATTENDANCE ════ */}
            {tab==="attendance"&&(
                <div className="pc-section">
                    {canManageProductivitySettings&&(
                        <>
                        {/* Attendance Method */}
                        <div className="pc-card" style={{marginBottom:"1.25rem"}}>
                            <div className="pc-card-head"><Clock size={15} color="var(--accent-primary)"/><h3>Attendance Method</h3></div>
                            <p className="pc-card-desc">Choose how employee attendance is recorded.</p>
                            <div className="pc-att-method-row">
                                <label
                                    className={`pc-att-method-card${attendanceMethod==="url_trigger"?" pc-att-method-card--active":""}`}
                                    onClick={()=>saveAttendanceMethod("url_trigger")}
                                >
                                    <div className="pc-att-method-radio">
                                        <span className={`pc-radio-dot${attendanceMethod==="url_trigger"?" pc-radio-dot--on":""}`}/>
                                    </div>
                                    <div>
                                        <div className="pc-att-method-title">URL Trigger</div>
                                        <div className="pc-att-method-desc">Marked present when employee visits a configured URL during work hours.</div>
                                    </div>
                                </label>
                                <label
                                    className={`pc-att-method-card${attendanceMethod==="auto"?" pc-att-method-card--active":""}`}
                                    onClick={()=>saveAttendanceMethod("auto")}
                                >
                                    <div className="pc-att-method-radio">
                                        <span className={`pc-radio-dot${attendanceMethod==="auto"?" pc-radio-dot--on":""}`}/>
                                    </div>
                                    <div>
                                        <div className="pc-att-method-title">Auto (Agent Online)</div>
                                        <div className="pc-att-method-desc">Marked present automatically when the agent connects during work hours.</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="pc-two-col">
                        {/* Attendance trigger URLs */}
                        <div className="pc-card">
                            <div className="pc-card-head"><Globe size={15} color="var(--accent-primary)"/><h3>Attendance Trigger URLs</h3></div>
                            <p className="pc-card-desc">
                                Visiting any of these URLs marks the user as present for the day.
                            </p>
                            <div className="pc-trigger-input-row">
                                <input
                                    type="text"
                                    placeholder="e.g. app.companyname.com"
                                    value={triggerInput}
                                    onChange={e=>setTriggerInput(e.target.value)}
                                    onKeyDown={e=>e.key==="Enter"&&addTrigger()}
                                />
                                <button className="pc-btn primary" onClick={addTrigger}><Plus size={13}/> Add</button>
                            </div>
                            <div className="pc-trigger-tags">
                                {triggerUrls.length===0&&<span className="pc-muted" style={{fontSize:"0.8rem"}}>No trigger URLs set.</span>}
                                {triggerUrls.map((u,i)=>(
                                    <span key={i} className="pc-trigger-tag">
                                        {u}
                                        <button onClick={()=>setTriggerUrls(prev=>prev.filter((_,j)=>j!==i))}>
                                            <X size={10}/>
                                        </button>
                                    </span>
                                ))}
                            </div>
                            {triggerUrls.length>0&&(
                                <button className="pc-btn primary pc-full-btn" style={{marginTop:12}} onClick={saveTriggers}>
                                    Save Trigger URLs
                                </button>
                            )}
                        </div>

                        {/* Work hours */}
                        <div className="pc-card">
                            <div className="pc-card-head"><Clock size={15} color="var(--accent-primary)"/><h3>Work Hours</h3></div>
                            <p className="pc-card-desc">Define when your team's work day starts and ends.</p>
                            {attLoading?(
                                <div className="pc-empty"><RefreshCw size={18} className="pc-spin" style={{opacity:0.4}}/></div>
                            ):(
                                <>
                                    <div className="pc-time-row">
                                        {[["Start",workStart,setWorkStart],["End",workEnd,setWorkEnd]].map(([lbl,val,set])=>(
                                            <div key={lbl} className="pc-time-field">
                                                <label className="pc-label">{lbl}</label>
                                                <input type="time" value={val} onChange={e=>set(e.target.value)}/>
                                            </div>
                                        ))}
                                    </div>
                                    <label className="pc-label" style={{marginTop:14,marginBottom:8,display:"block"}}>Working Days</label>
                                    <div className="pc-day-row">
                                        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d,i)=>(
                                            <button key={i}
                                                className={`pc-day-chip${workDays.includes(i)?" active":""}`}
                                                onClick={()=>setWorkDays(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i].sort())}>
                                                {d}
                                            </button>
                                        ))}
                                    </div>
                                    <button className="pc-btn primary pc-full-btn" style={{marginTop:14}} onClick={saveHours}>
                                        Save Work Hours
                                    </button>
                                </>
                            )}
                        </div>
                        </div>

                        {/* Reset today's attendance for an agent */}
                        <div className="pc-card" style={{marginTop:"1rem",maxWidth:520}}>
                            <div className="pc-card-head"><RefreshCw size={15} color="var(--color-warning)"/><h3>Reset Today's Attendance</h3></div>
                            <p className="pc-card-desc">Clears today's check-in/out record for one agent.</p>
                            <div className="pc-inline-row" style={{flexWrap:"wrap",gap:8,marginBottom:8}}>
                                <span className="gc-filter-search" style={{maxWidth:"none",flex:1,minWidth:160}}>
                                    <Search size={13}/>
                                    <input
                                        placeholder="Search agent…"
                                        value={resetSearch}
                                        onChange={e=>setResetSearch(e.target.value)}
                                    />
                                </span>
                                {depts.length>1&&(
                                    <PillSelect value={resetTeamId} onChange={e=>setResetTeamId(e.target.value)}>
                                        <option value="all">All Teams</option>
                                        {depts.map(d=><option key={d._id} value={d.name}>{d.name}</option>)}
                                    </PillSelect>
                                )}
                            </div>
                            {resetSearch.trim()?(
                                <div className="pc-agent-picker" style={{marginBottom:8}}>
                                    {resetFilteredAgents.length===0&&(
                                        <p className="pc-muted" style={{margin:"6px 9px"}}>No agents match this filter.</p>
                                    )}
                                    {resetFilteredAgents.map(a=>{
                                        const aid = a.agentId||a._id;
                                        return (
                                            <button
                                                key={aid}
                                                type="button"
                                                className={`pc-agent-picker-row${resetAgentId===aid?" active":""}`}
                                                onClick={()=>setResetAgentId(aid)}
                                            >
                                                {displayName(a)}
                                            </button>
                                        );
                                    })}
                                </div>
                            ):(
                                <p className="pc-muted" style={{margin:"0 0 8px"}}>Start typing an agent name to search…</p>
                            )}
                            <div className="pc-inline-row" style={{flexWrap:"wrap",gap:8}}>
                                <button className="pc-btn danger" disabled={!resetAgentId||resetting} onClick={resetTodayAttendance}>
                                    <RefreshCw size={13} className={resetting?"pc-spin":""}/> Reset
                                </button>
                            </div>
                        </div>
                        </>
                    )}
                    {!canManageProductivitySettings&&(
                        <div className="pc-empty"><Lock size={24} style={{opacity:0.2,marginBottom:8}}/><p>Attendance settings require admin access.</p></div>
                    )}
                </div>
            )}

            {/* ════ SCREENSHOTS ════ */}
            {tab==="screenshots"&&(
                <div className="pc-section">
                    <ScreenshotSettings />
                </div>
            )}

            {/* ════ AUTHENTICATION / SSO ════ */}
            {tab==="sso"&&(
                <div className="pc-section">
                    <SsoSettings />
                </div>
            )}

            {/* ════ INTEGRATIONS ════
            commented out — not ready yet, keep for later (see also the
            commented-out TABS entry above)
            {tab==="integrations"&&(
                <div className="pc-section">
                    <div className="pc-section-bar">
                        <span className="pc-section-title">Integrations</span>
                    </div>
                    <div className="pc-integrations-grid">
                        {INTEGRATIONS.map(i=>(
                            <div key={i.key} className="pc-card pc-integration-card">
                                <div className="pc-integration-icon"><i.icon size={22}/></div>
                                <span className="pc-integration-name">{i.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            */}

            {/* ════ MODALS ════ */}
            {showCreate&&(
                <div className="pc-overlay" onClick={()=>setShowCreate(false)}>
                    <div className="pc-modal" onClick={e=>e.stopPropagation()}>
                        <div className="pc-modal-head">
                            <h3>New Team</h3>
                            <button className="pc-btn" onClick={()=>setShowCreate(false)}><X size={15}/></button>
                        </div>
                        <div className="pc-modal-body">
                            <div className="pc-form-group">
                                <label>Team Name *</label>
                                <input type="text" placeholder="e.g. Engineering, HR, Sales"
                                    value={newTeam.name}
                                    onChange={e=>setNewTeam({...newTeam,name:e.target.value})}
                                    onKeyDown={e=>e.key==="Enter"&&handleCreateTeam()} autoFocus/>
                            </div>
                            <div className="pc-form-group">
                                <label>Description (optional)</label>
                                <textarea placeholder="What does this team do?"
                                    value={newTeam.description}
                                    onChange={e=>setNewTeam({...newTeam,description:e.target.value})}/>
                            </div>
                        </div>
                        <div className="pc-modal-foot">
                            <button className="pc-btn" onClick={()=>setShowCreate(false)}>Cancel</button>
                            <button className="pc-btn primary" onClick={handleCreateTeam}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {showAssign&&(
                <div className="pc-overlay" onClick={()=>setShowAssign(false)}>
                    <div className="pc-modal" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
                        <div className="pc-modal-head">
                            <h3>Assign Agents — {depts.find(d=>d._id===assignTeamId)?.name}</h3>
                            <button className="pc-btn" onClick={()=>setShowAssign(false)}><X size={15}/></button>
                        </div>
                        <div className="pc-modal-body" style={{maxHeight:"50vh",overflowY:"auto"}}>
                            {agents.length===0?(
                                <div className="pc-empty"><p>No agents connected yet.</p></div>
                            ):agents.map(agent=>{
                                const aid     = agent.agentId||agent._id;
                                const checked = assignIds.includes(aid);
                                const name    = agent.name||agent.hostname||agent.details?.hostname||aid;
                                const other   = depts.find(d=>d._id!==assignTeamId&&(d.agents||[]).includes(aid));
                                return (
                                    <label key={aid} className={`pc-agent-row${checked?" selected":""}`}>
                                        <input type="checkbox" checked={checked}
                                            onChange={()=>setAssignIds(p=>p.includes(aid)?p.filter(i=>i!==aid):[...p,aid])}/>
                                        <div style={{flex:1}}>
                                            <div style={{fontWeight:600,fontSize:"0.87rem"}}>{name}</div>
                                            <div style={{fontSize:"0.71rem",color:"var(--text-muted)"}}>
                                                {aid}{other&&<span style={{color:"var(--color-warning)",marginLeft:6}}>in: {other.name}</span>}
                                            </div>
                                        </div>
                                        <span className={`pc-status-chip${agent.status==="online"?" online":""}`}>
                                            {agent.status||"offline"}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="pc-modal-foot">
                            <span style={{fontSize:"0.77rem",color:"var(--text-muted)",marginRight:"auto"}}>{assignIds.length} selected</span>
                            <button className="pc-btn" onClick={()=>setShowAssign(false)}>Cancel</button>
                            <button className="pc-btn primary" onClick={saveAssign}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {showUrlModal&&(
                <div className="pc-overlay" onClick={()=>setShowUrlModal(false)}>
                    <div className="pc-modal" onClick={e=>e.stopPropagation()}>
                        <div className="pc-modal-head">
                            <h3>Add Website</h3>
                            <button className="pc-btn" onClick={()=>setShowUrlModal(false)}><X size={15}/></button>
                        </div>
                        <div className="pc-modal-body">
                            <div className="pc-form-group">
                                <label>Domain *</label>
                                <input type="text" placeholder="e.g. youtube.com"
                                    value={newUrl.domainName}
                                    onChange={e=>setNewUrl({...newUrl,domainName:e.target.value})} autoFocus/>
                            </div>
                            <div className="pc-form-group">
                                <label>Pattern Type</label>
                                <select value={newUrl.patternType} onChange={e=>setNewUrl({...newUrl,patternType:e.target.value})}>
                                    <option value="EXACT">Exact</option>
                                    <option value="WILDCARD">Wildcard (*.domain.com)</option>
                                    <option value="REGEX">Regex</option>
                                </select>
                            </div>
                            {newUrl.patternType!=="EXACT"&&(
                                <div className="pc-form-group">
                                    <label>Pattern</label>
                                    <input type="text"
                                        placeholder={newUrl.patternType==="WILDCARD"?"*.facebook.com":"^https?://.*"}
                                        value={newUrl.urlPattern}
                                        onChange={e=>setNewUrl({...newUrl,urlPattern:e.target.value})}/>
                                </div>
                            )}
                            <div className="pc-form-group">
                                <label>Category (optional)</label>
                                <input type="text" placeholder="e.g. Social Media"
                                    value={newUrl.category}
                                    onChange={e=>setNewUrl({...newUrl,category:e.target.value})}/>
                            </div>
                        </div>
                        <div className="pc-modal-foot">
                            <button className="pc-btn" onClick={()=>setShowUrlModal(false)}>Cancel</button>
                            <button className="pc-btn primary" onClick={async()=>{
                                if(!newUrl.domainName) return toast.error("Domain required");
                                const res=await apiPost("/api/productivity/urls",newUrl).catch(()=>null);
                                const d=await res?.json().catch(()=>null);
                                if(d?.success){
                                    toast.success("Website added");
                                    setShowUrlModal(false);
                                    setNewUrl({domainName:"",urlPattern:"",patternType:"EXACT",category:""});
                                    fetchAll();
                                } else toast.error(d?.message||"Failed");
                            }}>Add</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
